import React from "react";
import { Box, Card, Chip, CircularProgress, Link, Stack, Typography } from "@mui/material";
import { getAlmacenColor, getBadgeStyles } from "../../../../components/ui/badgeColors";
import Utils from "../../../../utils/Utils";
import AccionesEstadoVenta from "../ui/AccionesEstadoVenta";
import EstadoVentaBadge from "../ui/EstadoVentaBadge";
import {
    COLOR_FILA_MOSTRADOR,
    getEnlaceInfoEstadoVenta,
    normalizarCodigoAlmacen,
} from "../../helpers/estadosVentas";
import { VALOR_VACIO, formatFecha, formatTexto } from "../../utils/formatters";

const SX_CARD = { border: "1px solid #dee2e6", boxShadow: "none", p: { xs: 1.5, sm: 2 } };
// Mismo resaltado que las filas de mostrador de la tabla
const SX_CARD_MOSTRADOR = {
    ...SX_CARD,
    backgroundColor: COLOR_FILA_MOSTRADOR.fondo,
    color: COLOR_FILA_MOSTRADOR.texto,
};
const SX_NUMERO = { fontWeight: 700, color: "#cc071e", fontSize: { xs: 14, sm: 15 } };
const SX_DATO = { fontSize: { xs: 12, sm: 13 }, color: "inherit" };
const SX_ETIQUETA = { fontWeight: 600, color: "inherit", opacity: 0.75 };
const SX_CABECERA = { mb: 1 };
const SX_PIE = { mt: 1, justifyContent: "flex-end" };
const SX_CENTRADO = { display: "flex", justifyContent: "center", py: 4 };
const SX_SPINNER = { color: "#cc071e" };
const SX_VACIO = { color: "#6c757d", textAlign: "center", py: 4 };
const NAVE_ESTILO = { height: 22, fontSize: 10 };

const Dato = ({ etiqueta, valor }) => (
    <Typography component="div" sx={SX_DATO}>
        <Box component="span" sx={SX_ETIQUETA}>{etiqueta}:</Box> {valor}
    </Typography>
);

const NaveDato = ({ fila }) =>
    fila.nave ? (
        <Chip
            label={fila.nave}
            size="small"
            sx={getBadgeStyles(getAlmacenColor(normalizarCodigoAlmacen(fila.almacen)), NAVE_ESTILO)}
        />
    ) : (
        VALOR_VACIO
    );

const TarjetaVenta = ({ fila, cambiandoId, onCambiarEstado }) => {
    const onCambiar = React.useCallback((estado) => onCambiarEstado(fila, estado), [onCambiarEstado, fila]);
    return (
        <Card sx={fila.esMostrador ? SX_CARD_MOSTRADOR : SX_CARD}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={SX_CABECERA}>
                <Link href={getEnlaceInfoEstadoVenta(fila)} underline="hover" sx={SX_NUMERO}>
                    {fila.numero}
                </Link>
                <EstadoVentaBadge estado={fila.estado} />
            </Stack>
            <Stack spacing={0.5}>
                <Dato etiqueta="Tipo" valor={Utils.toCapitalize(fila.tipo) || VALOR_VACIO} />
                <Dato etiqueta="Cliente" valor={formatTexto(fila.cliente)} />
                <Dato etiqueta="Ruta" valor={formatTexto(fila.ruta)} />
                <Dato etiqueta="Nave" valor={<NaveDato fila={fila} />} />
                <Dato etiqueta="Fecha albarán" valor={formatFecha(fila.fecha)} />
                {fila.preparado && <Dato etiqueta="Preparando" valor={fila.preparado} />}
                {fila.revisado && <Dato etiqueta="Revisando" valor={fila.revisado} />}
                {fila.camara && <Dato etiqueta="Cámara" valor={fila.camara} />}
            </Stack>
            <Stack direction="row" sx={SX_PIE}>
                <AccionesEstadoVenta
                    onCambiar={onCambiar}
                    disabled={cambiandoId === fila.id}
                    estadoActual={fila.estado}
                />
            </Stack>
        </Card>
    );
};

/**
 * Vista móvil del listado de estados de ventas: tarjetas con acciones, carga y vacío integrados
 */
const EstadosVentasMobileList = ({ rows = [], loading, cambiandoId = null, onCambiarEstado }) => {
    if (rows.length === 0) {
        return loading ? (
            <Box sx={SX_CENTRADO}><CircularProgress sx={SX_SPINNER} /></Box>
        ) : (
            <Typography sx={SX_VACIO}>No hay pedidos de venta pendientes</Typography>
        );
    }
    return (
        <Stack spacing={2}>
            {rows.map((fila) => (
                <TarjetaVenta
                    key={fila.id}
                    fila={fila}
                    cambiandoId={cambiandoId}
                    onCambiarEstado={onCambiarEstado}
                />
            ))}
        </Stack>
    );
};

export default React.memo(EstadosVentasMobileList);
